import React, { useContext, useCallback, useMemo } from 'react'
import { dateDifference } from 'lib/datetime/dateUtils'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Clickable from 'components/Common/Clickable/Clickable'
import Image from 'components/Common/Image'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import getOfferFeatureIcon from 'lib/offer/getOfferFeatureIcon'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import CruiseDealTermsConditionsModal from './CruiseDealTermsConditionsModal'
import { scrollToId } from 'lib/window/scrollUtils'

const ImageBackgroundWrapper = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 60%;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 80%;
    background: linear-gradient(to top, rgb(54 58 69), rgb(54 58 69 / 80%), rgb(54 58 69 / 0%));
    z-index: 1;
  }

  ${mediaQueryUp.tablet} {
    right: 0;
    width: 50%;
    height: 100%;

    &::before {
      top: 0;
      left: 0;
      width: ${rem(400)};
      height: 100%;
      background: linear-gradient(to right, rgb(54 58 69), rgb(54 58 69 / 0%));
    }
  }

  ${mediaQueryUp.desktop} {
    width: 60%;
  }
`

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  padding-top: ${rem(32)};
  padding-bottom: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(28)};
    padding-bottom: ${rem(40)};
    width: 50%;
  }

  ${mediaQueryUp.desktop} {
    width: 30%;
  }
`

const CruiseDealHeroWrapper = styled.div`
  position: relative;
  background-color: ${props => props.theme.palette.neutral.default.one};
`

const DEFAULT_HERO_IMAGE_ID = 'nmiblelbjmu4o2nj0m34'

interface Props {
  cruiseDeal: App.Cruises.CruiseDeal
}

function CruiseDealHero({ cruiseDeal }: Props) {
  const showModal = useContext(ModalContext)

  const handleScrollToOffers = useCallback(() => {
    scrollToId('cruise-deal-offer-list')
  }, [])

  const showTermsAndConditionsModal = useCallback(() => {
    showModal(<CruiseDealTermsConditionsModal
      termsAndConditions={cruiseDeal.termsAndConditions}
      onViewOffers={handleScrollToOffers}
    />)
  }, [showModal, cruiseDeal.termsAndConditions, handleScrollToOffers])

  const shouldShowCountdown = useMemo(() => {
    return dateDifference(new Date(cruiseDeal.endDate), new Date()).days < 5
  }, [cruiseDeal.endDate])

  const hasHighlightedInclusion = useMemo(() => {
    return cruiseDeal.inclusions.some((inclusion) => inclusion.isHighlighted)
  }, [cruiseDeal.inclusions])

  return <CruiseDealHeroWrapper>
    <ImageBackgroundWrapper>
      <Image alt="Hero image" id={cruiseDeal.imageId || DEFAULT_HERO_IMAGE_ID} fit="center" />
    </ImageBackgroundWrapper>

    <LayoutContainer size="xl">
      <ContentWrapper>
        <Group direction="vertical" gap={16}>
          <Image alt="Cruise line logo" id={cruiseDeal.cruiseLine.whiteImageId} height={75} />

          <Group direction="vertical" gap={8} horizontalAlign="start">
            <PromotionLabel variant="premium" icon={<SolidFireIcon />}>
              Special offer
            </PromotionLabel>

            <Heading colour="neutral-eight" variant="heading2">
              {cruiseDeal.heading}
            </Heading>
          </Group>

          <Group direction="vertical" gap={8}>
            <Group direction="vertical" gap={4}>
              {cruiseDeal.inclusions.map((inclusion, index) => {
                // if there is no highlighted inclusion, the first inclusion should be highlighted
                const isHighlighted = inclusion.isHighlighted || (!hasHighlightedInclusion && index === 0)

                const Icon = getOfferFeatureIcon(inclusion.icon || 'check')

                return <BodyTextBlock
                  key={inclusion.id}
                  variant="medium"
                  weight={isHighlighted ? 'bold' : 'normal'}
                  colour={isHighlighted ? 'highlight-primary' : 'neutral-eight'}
                  startIcon={Icon ? <Icon /> : undefined}
                >
                  {inclusion.description}
                </BodyTextBlock>
              })}
            </Group>

            <Group direction="horizontal" gap={8} horizontalAlign="space-between" verticalAlign="center">
              <Group direction="vertical" tabletDirection="horizontal" gap={4} tabletGap={8}>
                {shouldShowCountdown && <OfferUrgencyCountdownLabel endDate={cruiseDeal.endDate} prefixLabel="Offer" tagPlace="cruise-deal-hero" />}

                <Group direction="vertical" tabletDirection="horizontal" tabletGap={8}>
                  {!shouldShowCountdown && <BodyText variant="medium" colour="neutral-eight">
                    Offer ends on <FormatDateTime value={cruiseDeal.endDate} format="dmy-casual-short" />
                  </BodyText>}

                  {!shouldShowCountdown && <CSSBreakpoint min="tablet">
                    <BodyText variant="medium" colour="neutral-eight">
                      {' · '}
                    </BodyText>
                  </CSSBreakpoint>}

                  <Clickable onClick={showTermsAndConditionsModal}>
                    <BodyText variant="medium" colour="neutral-eight" underline>
                      T&Cs apply
                    </BodyText>
                  </Clickable>
                </Group>
              </Group>

              <CSSBreakpoint only="mobile">
                <TextButton
                  kind="secondary"
                  variant="dark"
                  onClick={handleScrollToOffers}
                >
                  View offers
                </TextButton>
              </CSSBreakpoint>
            </Group>
          </Group>
        </Group>
      </ContentWrapper>
    </LayoutContainer>
  </CruiseDealHeroWrapper>
}

export default CruiseDealHero
